import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { SG_EDIT_DEBRIEF_SCHEDULE, SG_FETCH_DEBRIEF_SCHEDULE } from "constants/actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import BasicErrorMessage from "utilities/ErrorMessages";

export default function EditScheduleDialog({ open, onOpenChange, debriefDetails }) {
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [scheduledFor, setScheduledFor] = useState(null);
  const [closeAt, setCloseAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const { get_brief_schedules } = useSelector(
    (state) => ({
      get_brief_schedules: state.debrief_schedule,
    }),
    shallowEqual
  );

  // Initialize form values when dialog opens or debriefDetails changes
  useEffect(() => {
    if (debriefDetails && open) {
      setName(debriefDetails.name || "");
      setScheduledFor(debriefDetails.scheduled_for ? new Date(debriefDetails.scheduled_for) : null);
      setCloseAt(debriefDetails.close_at ? new Date(debriefDetails.close_at) : null);
    }
  }, [debriefDetails, open]);

  // Monitor loading state and success
  useEffect(() => {
    setLoading(get_brief_schedules?.pending);
    
    // If update was successful and this debrief was updated, close dialog and refetch
    if (debriefDetails?.id === get_brief_schedules?.updated && !get_brief_schedules?.pending) {
      dispatch({
        type: SG_FETCH_DEBRIEF_SCHEDULE,
        payload: { id: debriefDetails.id },
      });
      onOpenChange(false);
    }
  }, [get_brief_schedules, debriefDetails, dispatch, onOpenChange]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }

    const payload = {
      id: debriefDetails?.id,
      name: name.trim(),
    };

    if (scheduledFor) {
      payload.scheduled_for = scheduledFor;
    }

    if (closeAt) {
      payload.close_at = closeAt;
    }

    dispatch({
      type: SG_EDIT_DEBRIEF_SCHEDULE,
      payload,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Survey Schedule</DialogTitle>
          <DialogDescription>
            Update the name, scheduled date, and close date for this survey schedule.
          </DialogDescription>
        </DialogHeader>
        
        <BasicErrorMessage />
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter survey name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduled_for">Scheduled Date</Label>
            <DatePicker
              id="scheduled_for"
              selected={scheduledFor}
              onChange={(date) => setScheduledFor(date)}
              showTimeSelect
              dateFormat="MMMM d, yyyy h:mm aa"
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholderText="Select scheduled date and time"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="close_at">Close Date</Label>
            <DatePicker
              id="close_at"
              selected={closeAt}
              onChange={(date) => setCloseAt(date)}
              showTimeSelect
              dateFormat="MMMM d, yyyy h:mm aa"
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholderText="Select close date and time"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

